import {
  appendQuery,
  buildCellServiceUrl,
  type CellServiceQuery,
} from './cell-session';
import { isAbortError, withTokenAuthRecovery } from './cors-auth-recovery';

// =============================================================================
// Client base
// =============================================================================
//
// Direct browser-side calls to ``https://{cellId}.api.tranquility.ai``
// ``/entity-service/v1/extract/*``. This is the only client for the
// entity-service: the legacy ``/api/protected/entity`` BFF route and its
// server-side helper (``shared/entity-api-utils.ts``) have been removed.
// All callers (currently ``getEventsFromEntityService`` in ``app/main.tsx``)
// dispatch to the endpoint helpers below using the access token resolved by
// ``useCellServiceSession``.

/** Path prefix for every entity-service v1 endpoint. */
const API_PREFIX = '/entity-service/v1';

/**
 * Error thrown by every direct-CORS call to entity-service. Carries the
 * upstream HTTP status (when available) and the raw response body for
 * downstream loggers.
 */
export class EntityApiError extends Error {
  public readonly status: number | null;
  public readonly detail: string | null;

  constructor(message: string, status: number | null = null, detail: string | null = null) {
    super(message);
    this.name = 'EntityApiError';
    this.status = status;
    this.detail = detail;
  }
}

/**
 * Build a fully-qualified entity-service URL for a given cell.
 *
 * ``path`` is the route *after* ``/entity-service/v1`` (e.g.
 * ``"/extract/jobs"``). A leading slash is optional.
 */
export function buildEntityUrl(cellId: string, path: string): string {
  return buildCellServiceUrl(cellId, API_PREFIX, path);
}

/** Query map: scalar values are emitted once; arrays are emitted as repeated keys. */
export type EntityQuery = CellServiceQuery;

type EntitySessionArgs = {
  /** ``custom:cell_id`` claim, used to build the per-cell API host. */
  cellId: string;
  /**
   * Real Cognito access token (``token_use: "access"``). In this app that's
   * ``AuthContext.realAccessToken`` / ``features.tokens.rawAccessToken`` —
   * NOT ``AuthContext.accessToken`` (which is the ID token for legacy BFF
   * cookie reasons and would be rejected by the Lambda Authorizer).
   */
  accessToken: string;
};

type EntityFetchArgs = EntitySessionArgs & {
  /** Path under ``/entity-service/v1`` (e.g. ``"/extract/jobs"``). */
  path: string;
  /** Optional query map; arrays produce repeated keys. */
  query?: EntityQuery;
  signal?: AbortSignal;
};

type EntityRequestArgs = EntityFetchArgs & {
  /** HTTP method. ``"GET"`` defaults via ``entityFetch``; this helper covers POST/DELETE/etc. */
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /**
   * Request body. ``undefined`` is sent as no body (used by GET/DELETE).
   * Anything else is JSON-stringified.
   */
  body?: unknown;
};

function assertSession(cellId: string, accessToken: string): void {
  if (!cellId) {
    throw new EntityApiError('Missing cellId (custom:cell_id claim) on the ID token.');
  }
  if (!accessToken) {
    throw new EntityApiError('Missing access token.');
  }
}

async function readErrorAndThrow(res: Response, path: string): Promise<never> {
  const detail = await res.text().catch(() => '');
  throw new EntityApiError(
    `entity-service ${path} returned ${res.status}`,
    res.status,
    detail || null,
  );
}

/**
 * Generic GET helper for entity-service. Validates the session inputs,
 * issues the request, and either returns parsed JSON typed as ``T`` or throws
 * an ``EntityApiError``.
 */
export async function entityFetch<T>(args: EntityFetchArgs): Promise<T> {
  const { cellId, path, query, signal } = args;
  assertSession(cellId, args.accessToken);

  const url = appendQuery(buildEntityUrl(cellId, path), query);

  return withTokenAuthRecovery(args.accessToken, async (accessToken) => {
    let res: Response;
    try {
      res = await fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        signal,
      });
    } catch (error) {
      // A caller-cancelled request is not a failure. Wrapping it would match
      // isLikelyCorsOrNetworkFailure and trigger a token refresh for a cancel.
      if (isAbortError(error)) throw error;
      throw new EntityApiError(
        `Network error calling entity-service ${path}: ${(error as Error).message}`,
      );
    }

    if (!res.ok) {
      await readErrorAndThrow(res, path);
    }

    return (await res.json().catch(() => null)) as T;
  });
}

/**
 * Generic non-GET helper for entity-service (POST/PUT/PATCH/DELETE).
 *
 * - 204 responses return ``null`` cast as ``T`` so DELETE callers can keep a
 *   ``Promise<void>``-shaped surface without special-casing the body parse.
 * - Non-204 responses are parsed as JSON and returned typed as ``T``.
 */
export async function entityRequest<T>(args: EntityRequestArgs): Promise<T> {
  const { cellId, path, method, body, query, signal } = args;
  assertSession(cellId, args.accessToken);

  const url = appendQuery(buildEntityUrl(cellId, path), query);

  const hasBody = body !== undefined;

  return withTokenAuthRecovery(args.accessToken, async (accessToken) => {
    let res: Response;
    try {
      res = await fetch(url, {
        method,
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${accessToken}`,
          ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
        },
        body: hasBody ? JSON.stringify(body) : undefined,
        signal,
      });
    } catch (error) {
      // A caller-cancelled request is not a failure. Wrapping it would match
      // isLikelyCorsOrNetworkFailure and trigger a token refresh for a cancel.
      if (isAbortError(error)) throw error;
      throw new EntityApiError(
        `Network error calling entity-service ${path}: ${(error as Error).message}`,
      );
    }

    if (!res.ok) {
      await readErrorAndThrow(res, path);
    }

    if (res.status === 204) {
      return null as unknown as T;
    }

    return (await res.json().catch(() => null)) as T;
  });
}

// =============================================================================
// Response shapes
// =============================================================================

/**
 * Loose shape of an extraction job returned by ``/extract/jobs`` endpoints.
 * Kept permissive because the existing UI handlers in ``app/main.tsx`` only
 * inspect ``jobId`` / ``status`` / ``result.events`` / ``epochTimeSecs`` and
 * tolerate additional upstream fields.
 */
export interface ExtractionJob {
  jobId?: string;
  status?: 'pending' | 'completed' | 'failed' | 'deleted' | (string & {});
  result?: { events?: unknown[] } & Record<string, unknown>;
  epochTimeSecs?: number;
  [key: string]: unknown;
}

/**
 * Loose shape of the synchronous events response returned by
 * ``POST /extract/``. The UI consumes ``events`` directly when present;
 * other top-level fields are passed through unchanged.
 */
export interface ExtractEventsResponse {
  events?: unknown[];
  [key: string]: unknown;
}

// =============================================================================
// Endpoint helpers
// =============================================================================

type CreateExtractionJobArgs = EntitySessionArgs & {
  /** Full chat answer text (sent verbatim — no chat-history hydration server-side). */
  text: string;
  /** Job tags (e.g. ``["CASE:<caseId>"]``) used for later ``/extract/jobs/query`` lookups. */
  tags: string[];
  /** Source URLs (already flattened from ``Source.sourceUrl`` by the caller). */
  sources: string[];
  /** Original chat-answer epoch — entity-service uses it as the job correlation key. */
  epochTimeSecs: number;
  signal?: AbortSignal;
};

/**
 * ``POST /extract/jobs`` — kick off an asynchronous extraction job. The
 * returned job carries ``jobId`` + ``status: 'pending'``; callers poll
 * ``getExtractionJobStatus`` until the status flips to ``completed`` /
 * ``failed`` / ``deleted``.
 */
export async function createExtractionJob(args: CreateExtractionJobArgs): Promise<ExtractionJob> {
  const { cellId, accessToken, text, tags, sources, epochTimeSecs, signal } = args;
  return entityRequest<ExtractionJob>({
    cellId,
    accessToken,
    path: '/extract/jobs',
    method: 'POST',
    body: { text, tags, sources, epochTimeSecs },
    signal,
  });
}

type ExtractionJobByIdArgs = EntitySessionArgs & {
  jobId: string;
  signal?: AbortSignal;
};

/** ``GET /extract/jobs/{jobId}`` — fetch a single job's current state. */
export async function getExtractionJobStatus(
  args: ExtractionJobByIdArgs,
): Promise<ExtractionJob> {
  const { cellId, accessToken, jobId, signal } = args;
  return entityFetch<ExtractionJob>({
    cellId,
    accessToken,
    path: `/extract/jobs/${encodeURIComponent(jobId)}`,
    signal,
  });
}

/**
 * ``DELETE /extract/jobs/{jobId}`` — drop a completed / cancelled job.
 * entity-service returns 204; this helper resolves to ``void``.
 */
export async function deleteExtractionJob(args: ExtractionJobByIdArgs): Promise<void> {
  const { cellId, accessToken, jobId, signal } = args;
  await entityRequest<null>({
    cellId,
    accessToken,
    path: `/extract/jobs/${encodeURIComponent(jobId)}`,
    method: 'DELETE',
    signal,
  });
}

type QueryExtractionJobsArgs = EntitySessionArgs & {
  /**
   * Tag filter array (e.g. ``["CASE:<caseId>"]``). Sent as the **bare** JSON
   * array — entity-service ``POST /extract/jobs/query`` expects the tag list
   * at the top level, not wrapped in an object. See entity-service
   * ``test_extraction_jobs.py`` (``json=["CASE:..."]``).
   */
  tags: string[];
  signal?: AbortSignal;
};

/**
 * ``POST /extract/jobs/query`` — list jobs matching the given tags.
 * Empty input would return *all* jobs; callers should guard against that
 * upstream (``getEventsFromEntityService`` always passes a populated tag list).
 */
export async function queryExtractionJobs(
  args: QueryExtractionJobsArgs,
): Promise<ExtractionJob[]> {
  const { cellId, accessToken, tags, signal } = args;
  const body = await entityRequest<unknown>({
    cellId,
    accessToken,
    path: '/extract/jobs/query',
    method: 'POST',
    body: tags,
    signal,
  });
  return Array.isArray(body) ? (body as ExtractionJob[]) : [];
}

type ExtractEventsArgs = EntitySessionArgs & {
  /** Full chat answer text (sent verbatim — no chat-history hydration server-side). */
  text: string;
  /** Job tags (e.g. ``["CASE:<caseId>"]``). Mirrored on the synchronous extract endpoint. */
  tags: string[];
  /** Source URLs (already flattened from ``Source.sourceUrl`` by the caller). */
  sources: string[];
  signal?: AbortSignal;
};

/**
 * ``POST /extract/`` — synchronous extraction: returns events without going
 * through the job lifecycle. Used today by ``GET_EVENTS`` to back-fill events
 * for a chat answer that was previously rendered without the timeline tab.
 */
export async function extractEvents(args: ExtractEventsArgs): Promise<ExtractEventsResponse> {
  const { cellId, accessToken, text, tags, sources, signal } = args;
  return entityRequest<ExtractEventsResponse>({
    cellId,
    accessToken,
    path: '/extract/',
    method: 'POST',
    body: { text, tags, sources },
    signal,
  });
}

// =============================================================================
// Query keys
// =============================================================================

/**
 * Hierarchical TanStack Query key factory for every entity-service resource.
 * Provided for parity with ``junkdrawerKeys`` / ``provenanceKeys`` even
 * though the current caller (``getEventsFromEntityService`` in
 * ``app/main.tsx``) is imperative; a follow-up that adopts ``useQuery`` for
 * job polling can key off these without re-deriving them.
 */
export const entityKeys = {
  all: ['entity'] as const,

  jobs: () => [...entityKeys.all, 'jobs'] as const,
  jobById: (cellId: string | null, jobId: string) =>
    [...entityKeys.jobs(), 'byId', cellId, jobId] as const,
  jobsByTags: (cellId: string | null, tags: readonly string[]) =>
    [...entityKeys.jobs(), 'byTags', cellId, tags] as const,

  extractEvents: () => [...entityKeys.all, 'extractEvents'] as const,
  extractEventsForRequest: (
    cellId: string | null,
    tags: readonly string[],
    text: string,
    sources: readonly string[],
  ) => [...entityKeys.extractEvents(), cellId, tags, text, sources] as const,
};
